/**
 * Forgot-password page (spec 0009 §5.8).
 *
 * POST /auth/password/forgot answers 202 for every plausible email, so
 * the confirmation copy is the same whether or not the address belongs
 * to an operator — the page must never hint otherwise.
 */
import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";
import axios from "axios";
import { postForgotPassword } from "@/lib/api";
import { AuthShell } from "@/components/auth/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

function errorCopy(err: unknown): string {
  const status = axios.isAxiosError(err) ? err.response?.status : undefined;
  if (status === 404) {
    return "Self-service reset isn't enabled. Ask a super admin to rotate your password.";
  }
  if (status === 429) {
    return "Too many reset requests from this network. Wait a while and try again.";
  }
  if (status === 400) return "Enter a valid email address.";
  return "Couldn't send the request. Check your network and try again.";
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await postForgotPassword({ email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      setError(errorCopy(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthShell
      title="Reset your password"
      description="Enter your account email and we'll send you a reset link."
      footer={
        <Link to="/login" className="text-foreground underline">
          Back to sign in
        </Link>
      }
    >
      {sent ? (
        <div
          role="status"
          className="rounded-md border border-success/40 bg-success/10 p-3 text-sm text-success"
        >
          If that address belongs to an operator, a reset link is on its way. It
          expires in 30 minutes. Resetting your password doesn't change your
          authenticator.
        </div>
      ) : (
        <form className="space-y-4" onSubmit={onSubmit} noValidate>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
              placeholder="you@example.com"
            />
          </div>
          {error ? (
            <div
              role="alert"
              className="rounded-md border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
            >
              {error}
            </div>
          ) : null}
          <Button type="submit" className="w-full" disabled={submitting} size="lg">
            <Mail />
            {submitting ? "Sending…" : "Send reset link"}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

export default ForgotPasswordPage;
